import React, { useMemo, useRef } from 'react';
import Modal from 'react-modal';
import styles from './AlocacaoMensalModal.module.css';
import { exportarAlocacao } from '../../utils/exportarAlocacao';

Modal.setAppElement('#root');

const mesesNomes = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const AlocacaoMensalModal = ({ isOpen, onClose, dadosMensalidades = [], referencia, aluno = {} }) => {
  const contentRef = useRef();

  const mesMatricula = aluno.mes_matricula || 8; 
  const anoMatricula = aluno.ano_matricula || new Date().getFullYear();

  // Monta lista de 12 meses
  const mensalidadesCompletas = useMemo(() => {
    const lista = [];
    for (let m = 1; m <= 12; m++) {
      const item = dadosMensalidades.find(d => d.mes === m) || {};
      const inativo = m < mesMatricula;

      lista.push({
        ano: anoMatricula,
        mes: m,
        mesNome: mesesNomes[m],
        status: item.status || (inativo ? 'inativo' : 'pendente'),
        valor: item.valor || 0,
        multa: item.multa || 0,
        valor_pago: item.valor_pago || 0,
        atualizado_em: 
          (item.status === 'pago' || m === mesMatricula) 
            ? (item.atualizado_em || item.data_movimento || new Date()) 
            : null,
        observacao: item.observacao || (inativo ? (m === mesMatricula ? 'Mês da matrícula' : 'Mês anterior à cobrança') : ''),
        inativo
      });
    }
    return lista;
  }, [dadosMensalidades, mesMatricula, anoMatricula]);

  // Totais considerando apenas meses ativos
  const totais = useMemo(() => {
    return mensalidadesCompletas.reduce((acc, item) => {
      if (!item.inativo) {
        acc.valorBase += Number(item.valor) || 0;
        acc.multa += Number(item.multa) || 0;
        acc.valorPago += Number(item.valor_pago) || 0;
        if (item.status === 'pendente') acc.pendentes += 1;
      }
      return acc;
    }, { valorBase: 0, multa: 0, valorPago: 0, pendentes: 0 });
  }, [mensalidadesCompletas]);

  const exportarPDF = () => {
    exportarAlocacao(aluno, referencia, mensalidadesCompletas, totais, 'pdf');
  };

  const formatarValor = (valor) => {
    const numero = parseFloat(valor);
    return isNaN(numero) ? '0.00' : numero.toFixed(2);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <div ref={contentRef}>
        <h2 className={styles.titulo}>Alocação por Mês</h2>
        <p className={styles.referencia}>
          <strong>Aluno:</strong> {aluno.nome || '—'} ({aluno.codigo || 'sem código'})<br />
          <strong>Referência:</strong> {referencia}
        </p>

        {totais.pendentes > 0 && (
          <div className={styles.alerta}>
            ⚠️ Existem <strong>{totais.pendentes}</strong> parcelas ainda <strong>pendentes</strong>.
          </div>
        )}

        <div className={styles.tabelaWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Ano</th>
                <th>Mês</th>
                <th>Status</th>
                <th>Valor Alocado</th>
                <th>Multa</th>
                <th>Total Pago</th>
                <th>Data de Movimento</th>
                <th>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {mensalidadesCompletas.length === 0 ? (
                <tr>
                  <td colSpan="8" className={styles.semDados}>Nenhuma alocação encontrada.</td>
                </tr>
              ) : (
                mensalidadesCompletas.map((mens, idx) => (
                  <tr
                    key={idx}
                    className={`${mens.multa > 0 ? styles.comMulta : ''} ${mens.inativo ? styles.inativo : ''}`}
                    style={mens.inativo ? { color: '#999', cursor: 'not-allowed' } : {}}
                    title={mens.inativo ? 'Mês anterior à matrícula - inativo' : ''}
                  >
                    <td>{mens.ano || '—'}</td>
                    <td>{mens.mesNome || `Mês ${mens.mes}`}</td>
                    <td className={styles[`status_${(mens.status || '').toLowerCase()}`]}>{mens.status || '—'}</td>
                    <td>{formatarValor(mens.valor)} MZN</td>
                    <td>{mens.multa ? `${formatarValor(mens.multa)} MZN` : '—'}</td>
                    <td>{formatarValor(mens.valor_pago)} MZN</td>
                    <td>{mens.atualizado_em ? new Date(mens.atualizado_em).toLocaleDateString() : '—'}</td>
                    <td>{mens.observacao || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
            {mensalidadesCompletas.length > 0 && (
              <tfoot>
                <tr className={styles.totalRow}>
                  <td colSpan="3">Totais</td>
                  <td>—</td>
                  <td>{Number(totais.multa || 0).toFixed(2)} MZN</td>
                  <td>{Number(totais.valorPago || 0).toFixed(2)} MZN</td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className={styles.actions}>
        <button onClick={exportarPDF} className={styles.botaoExportar}>Exportar PDF</button>
        <button onClick={onClose} className={styles.botaoFechar}>Fechar</button>
      </div>
    </Modal>
  );
};

export default AlocacaoMensalModal;
